import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { AddUserDialog } from "@/components/registrant/dashboard/add-user-dialog";
import { Button } from "@/components/ui/button";
import ErrorComponent from "@/components/ui/error";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import {
  type OmtAmsAccountUser,
  addOmtAmsSecondaryUser,
  cancelOmtAmsSecondaryUserInvite,
  disableOmtAmsSecondaryUser,
  getOmtAmsAccountUsers,
  reenableOmtAmsSecondaryUser,
  updateOmtAmsAccountUserAdmin,
} from "@/hera-api/omt-ams-api";
import { formatAccountTypeForOrgUserList } from "@/lib/registrant-account-type-display";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, UserCheck, UserX, XCircle } from "lucide-react";
import { useCallback, useState } from "react";

const OMT_AMS_USER_ACCESSOR_KEYS = [
  "email",
  "name",
  "account_type",
  "admin",
] as const;

type OmtAmsUserAccessorKeys = (typeof OMT_AMS_USER_ACCESSOR_KEYS)[number];

function AdminToggle({
  row,
  disabled,
}: {
  row: OmtAmsAccountUser;
  disabled: boolean;
}) {
  const queryClient = useQueryClient();
  const isPrimary = row.account_type !== "SECONDARY";
  const locked =
    disabled || row.status === "pending" || row.status === "disabled";
  const mutation = useMutation({
    mutationFn: (isAdmin: boolean) =>
      updateOmtAmsAccountUserAdmin(row.id, isAdmin),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["omt-ams-users"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  return (
    <div
      className="flex items-center gap-2 text-sm"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Switch
        id={`omt-admin-${row.id}`}
        checked={row.is_admin || isPrimary}
        disabled={locked || isPrimary || mutation.isPending}
        onCheckedChange={(checked) => mutation.mutate(checked)}
        aria-label={`Admin ${row.email}`}
      />
      <Label htmlFor={`omt-admin-${row.id}`} className="font-normal">
        {row.is_admin || isPrimary ? "Enabled" : "Disabled"}
      </Label>
    </div>
  );
}

function getOmtAmsUserColumnProperties(
  accessorKey: OmtAmsUserAccessorKeys,
): ColumnProperties<OmtAmsAccountUser, OmtAmsUserAccessorKeys> {
  switch (accessorKey) {
    case "email":
      return {
        displayName: "Email",
        defaultVisible: true,
        getValue: (row) => row.email ?? "",
      };
    case "name":
      return {
        displayName: "Name",
        defaultVisible: true,
        getValue: (row) => (row.status === "pending" ? "" : (row.name ?? "")),
      };
    case "account_type":
      return {
        displayName: "Account Type",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => {
          if (row.status === "pending") return "Pending";
          if (row.status === "disabled") return "Disabled";
          return formatAccountTypeForOrgUserList(row.account_type);
        },
      };
    case "admin":
      return {
        displayName: "Admin",
        defaultVisible: true,
        defaultWidth: 140,
        allowBlankFilter: false,
        getValue: (row) =>
          row.is_admin || row.account_type !== "SECONDARY"
            ? "Enabled"
            : "Disabled",
        renderCell: (row) => <AdminToggle row={row} disabled={false} />,
      };
    default: {
      const _exhaustive: never = accessorKey;
      return _exhaustive;
    }
  }
}

export function OmtAmsDashboardUsers({
  isAuthSetupComplete,
  leadingContent,
}: {
  isAuthSetupComplete: boolean;
  leadingContent?: React.ReactNode;
}) {
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["omt-ams-users"],
    queryFn: getOmtAmsAccountUsers,
    enabled: isAuthSetupComplete,
  });

  const invalidateUsers = () => {
    void queryClient.invalidateQueries({ queryKey: ["omt-ams-users"] });
  };

  const addUser = useCallback(async (email: string) => {
    await addOmtAmsSecondaryUser(email);
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          data-slot="dashboard-table-scroll"
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {leadingContent}
          <LoadingScreen message="Fetching users..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          data-slot="dashboard-table-scroll"
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {leadingContent}
          <ErrorComponent
            title={getQueryErrorHeading(error)}
            message={getQueryErrorMessage(error)}
          />
        </div>
      </div>
    );
  }

  const users = data?.users ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0">
      <DashboardTable<OmtAmsAccountUser, OmtAmsUserAccessorKeys>
        data={users}
        lockToViewport
        tablePersistenceKey="omt-ams-users"
        leadingContent={
          <div className="pb-4 space-y-4">
            {leadingContent}
            {successMessage ? (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4">
                <p className="text-sm">{successMessage}</p>
              </div>
            ) : null}
            <div className="flex justify-end">
              <Button onClick={() => setIsAddUserDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>
        }
        accessorKeys={OMT_AMS_USER_ACCESSOR_KEYS}
        getColumnProperties={getOmtAmsUserColumnProperties}
        getRowId={(row) => row.id}
        initialSortColumn="email"
        initialSortDirection="asc"
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        rowActionsColumnWidth={140}
        renderRowActions={(row) => {
          if (row.status === "pending") {
            return (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await cancelOmtAmsSecondaryUserInvite(row.id);
                    invalidateUsers();
                  } catch (inviteError) {
                    toast({
                      title: "Error",
                      description: getQueryErrorMessage(inviteError),
                      variant: "destructive",
                    });
                  }
                }}
              >
                <XCircle className="h-4 w-4" />
                Cancel
              </Button>
            );
          }
          if (row.status === "active" && row.account_type === "SECONDARY") {
            return (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await disableOmtAmsSecondaryUser(row.id);
                    invalidateUsers();
                  } catch (disableError) {
                    toast({
                      title: "Error",
                      description: getQueryErrorMessage(disableError),
                      variant: "destructive",
                    });
                  }
                }}
              >
                <UserX className="h-4 w-4" />
                Disable
              </Button>
            );
          }
          if (row.status === "disabled") {
            return (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await reenableOmtAmsSecondaryUser(row.id);
                    invalidateUsers();
                  } catch (reenableError) {
                    toast({
                      title: "Error",
                      description: getQueryErrorMessage(reenableError),
                      variant: "destructive",
                    });
                  }
                }}
              >
                <UserCheck className="h-4 w-4" />
                Re-enable
              </Button>
            );
          }
          return null;
        }}
      />

      <AddUserDialog
        isOpen={isAddUserDialogOpen}
        onOpenChange={setIsAddUserDialogOpen}
        onSubmit={addUser}
        onSuccess={(email) => {
          invalidateUsers();
          setSuccessMessage(`Invite sent to ${email}`);
        }}
      />
    </div>
  );
}
